import React, { useState, useEffect } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { VscLoading } from 'react-icons/vsc';
import { fetchWrapper } from '@/_helpers';
import { toast } from 'react-toastify';
import {
    ErrorText,
    FormContainer,
    FormGroup,
    FormTitle,
    Input,
    LoadingMessage,
    Label,
} from '../../styles/EVVDataBlockStyles';
import { BasicInformationformSchema as formSchema } from '../../formSchema/provider.formSchema';

const BLOCK_IDENTIFIER = 'provider_basic_information';
export const getInitialFormSchemaData = () => {
    const initialData = {};
    Object.keys(formSchema || {}).forEach((key) => {
        initialData[key] = '';
    });
    return initialData;
};

const BasicInformation = ({ providerId, teamId }) => {
    const [data, setData] = useState(getInitialFormSchemaData());
    const [isBusy, setIsBusy] = useState(true);
    const [error, setError] = useState(null);
    const [responseData, setResponseData] = useState(null);
    const fetchData = async () => {
        if (!providerId) {
            return setError('Failed to load data. Please try again later.');
        }
        try {
            const response = await fetchWrapper.get(
                `evv-datablock/provider/${providerId}/${BLOCK_IDENTIFIER}${teamId ? `?team=${teamId}` : ''}`
            );
            setResponseData(response);
            setData(response?.data || {});
        } catch (err) {
            if (err?.name === 'AbortError') return;
            if (err && err?.code === 404) {
                // skip error message if data not found
            } else {
                toast.error(err?.message || 'Something went wrong');
                console.log('response', err);
            }
        } finally {
            setIsBusy(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsBusy(true);
            let response;
            if (responseData) {
                response = await fetchWrapper.put(
                    `evv-datablock/provider/${providerId}/${BLOCK_IDENTIFIER}?team=${teamId}`,
                    { data }
                );
            } else {
                response = await fetchWrapper.post(
                    `evv-datablock/provider/${providerId}/${BLOCK_IDENTIFIER}?team=${teamId}`,
                    { data }
                );
            }
            if (!response?.data) {
                return toast.error('Failed to save data. Please try again later.');
            }
            setData(response?.data || {});
            toast.success('Data saved successfully');
        } catch (e) {
            toast.error(e?.message || 'Something went wrong');
        } finally {
            setIsBusy(false);
        }
    };

    const renderField = (fieldName, fieldSchema) => {
        const { type, label, placeholder, validation } = fieldSchema;
        const value = data[fieldName] || '';

        switch (type) {
            default:
                return (
                    <FormGroup key={fieldName}>
                        <Label htmlFor={fieldName}>
                            {label}
                            {validation?.required && (
                                <span className="ml-2" style={{ color: 'red' }}>
                                    *
                                </span>
                            )}
                        </Label>
                        <Input
                            id={fieldName}
                            name={fieldName}
                            type={type}
                            value={value}
                            onChange={handleChange}
                            placeholder={placeholder}
                            maxLength={validation?.maxLength}
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </FormGroup>
                );
        }
    };

    if (isBusy) {
        return (
            <LoadingMessage>
                <VscLoading className="spin" size={40} />
            </LoadingMessage>
        );
    }

    if (error) {
        return <Alert variant="danger">Failed to load data</Alert>;
    }

    return (
        <FormContainer>
            <FormTitle>Basic Information</FormTitle>
            <form onSubmit={handleSubmit}>
                {Object.keys(formSchema).map((key) => renderField(key, formSchema[key]))}
                <Button type="submit" disabled={isBusy} variant="primary">
                    {isBusy ? 'Saving...' : 'Save Changes'}
                </Button>
            </form>
        </FormContainer>
    );
};

export default BasicInformation;
